import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone, HostListener, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { NgbAlertModule, NgbTooltipModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { SafePipe } from '../../pipes/safe.pipe';
import { AuthService } from '../../services/auth.service';
import { AuthUser } from '../../interfaces/auth.interface';
import { environment } from '../../../environments/environment';
import {
  PaymentSourceType,
  PaymentVerificationRow
} from '../payment-verification/payment-verification.component';

/** สถานะที่ใช้กับใบคำขอ (เก็บเป็นข้อความ) */
const FORM_PAYMENT_STATUSES = [
  'รอชำระเงิน',
  'รอตรวจสอบยอดชำระ',
  'ชำระเงินเสร็จสิ้น',
  'ไม่ผ่านการตรวจสอบ',
  'ยกเลิกการชำระ'
];

/** การอบรม/สมัครสอบเก็บสถานะเป็นตัวเลข จึงมีให้เลือกน้อยกว่า */
const NUMERIC_PAYMENT_STATUSES = [
  'รอชำระเงิน',
  'รอตรวจสอบยอดชำระ',
  'ชำระเงินเสร็จสิ้น',
  'ยกเลิกการชำระ'
];

const TYPE_LABELS: Record<string, string> = {
  'member-card': 'ขอทำบัตรสมาชิก',
  'renew-member': 'ขอต่ออายุสมาชิก',
  'register-appraiser': 'ขอขึ้นทะเบียน',
  'renew-appraiser': 'ขอต่ออายุบัตร',
  training: 'การอบรม',
  exam: 'การสมัครสอบ'
};

/**
 * หน้ารายละเอียดกลางของเมนู "ตรวจสอบการชำระเงิน"
 *
 * ใช้ร่วมกันทุกประเภทรายการ แทนการเด้งไปหน้ารายละเอียดของแต่ละใบคำขอ
 * ฝ่ายการเงินตรวจสลิป ปรับสถานะการชำระเงิน และแก้ไขใบเสร็จได้จบในหน้าเดียว
 */
@Component({
  selector: 'app-payment-verification-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbAlertModule,
    NgbTooltipModule,
    StaffHeaderComponent,
    StaffNavComponent,
    SafePipe
  ],
  templateUrl: './payment-verification-detail.component.html',
  styleUrl: './payment-verification-detail.component.scss'
})
export class PaymentVerificationDetailComponent implements OnInit, OnDestroy {
  @ViewChild('receiptModal') receiptModal!: TemplateRef<any>;

  currentUser: AuthUser | null = null;
  isMobileMenuOpen = false;

  sourceType!: PaymentSourceType;
  recordId!: string;
  record: PaymentVerificationRow | null = null;

  isLoading = false;
  isSaving = false;
  error: string | null = null;
  successMessage: string | null = null;

  /** สถานะที่เลือกไว้บนหน้าจอ (ยังไม่บันทึกจนกว่าจะกด "บันทึกข้อมูล") */
  selectedStatus = '';
  cancelReason = '';

  // หน้าต่างแก้ไขใบเสร็จ
  receiptTransferDateTime = '';
  receiptTypeValue: 'individual' | 'company' = 'individual';
  isSavingReceipt = false;

  // หลักฐานการชำระเงิน (ย่อ-ขยายได้)
  showSlipPreview = false;
  slipPreviewUrl: string | null = null;
  slipPreviewIsPdf = false;
  slipZoom = 1;
  slipOffsetX = 0;
  slipOffsetY = 0;
  private slipDragging = false;
  private slipDragStartX = 0;
  private slipDragStartY = 0;
  private readonly SLIP_ZOOM_MIN = 0.25;
  private readonly SLIP_ZOOM_MAX = 5;
  private readonly SLIP_ZOOM_STEP = 0.25;

  private returnUrl: string | null = null;
  private subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.authService.currentUser$.subscribe(user => {
        this.currentUser = user;
        this.cdr.detectChanges();
      })
    );

    this.sourceType = this.route.snapshot.paramMap.get('type') as PaymentSourceType;
    this.recordId = this.route.snapshot.paramMap.get('id') || '';
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');

    this.loadRecord();
  }

  ngOnDestroy(): void {
    this.modalService.dismissAll();
    this.subscription.unsubscribe();
  }

  onLogout(): void {
    this.authService.logout();
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  onNavigate(route: string): void {
    this.closeMobileMenu();
  }

  // ── โหลดข้อมูล ──────────────────────────────────────────────────────────

  loadRecord(): void {
    if (!this.sourceType || !this.recordId) {
      this.error = 'ไม่พบข้อมูลรายการที่ต้องการดู';
      return;
    }

    this.isLoading = true;
    this.error = null;
    this.cdr.detectChanges();

    const apiUrl = `${environment.apiUrl}/payment-verification/${this.sourceType}/${this.recordId}`;
    const sub = this.http.get<PaymentVerificationRow>(apiUrl).subscribe({
      next: record => {
        this.ngZone.run(() => {
          this.record = record;
          this.selectedStatus = this.statusOptions.includes(record.paymentStatus)
            ? record.paymentStatus
            : this.statusOptions[0];
          this.cancelReason = '';
          this.isLoading = false;
          this.cdr.detectChanges();
        });
      },
      error: (err: HttpErrorResponse) => {
        this.ngZone.run(() => {
          console.error('❌ โหลดรายละเอียดการชำระเงินไม่สำเร็จ:', err);
          this.error = `เกิดข้อผิดพลาดในการโหลดข้อมูล: ${err.error?.message || err.message}`;
          this.isLoading = false;
          this.cdr.detectChanges();
        });
      }
    });
    this.subscription.add(sub);
  }

  // ── ข้อมูลสำหรับแสดงผล ──────────────────────────────────────────────────

  get typeLabel(): string {
    return TYPE_LABELS[this.sourceType] || this.sourceType;
  }

  /** ป้ายกำกับของ "ชื่อรายการ" ต่างกันตามประเภท (อบรมคือชื่อคอร์ส) */
  get itemTitleLabel(): string {
    switch (this.sourceType) {
      case 'training': return 'ชื่อคอร์ส';
      case 'exam': return 'ชื่อการสอบ';
      default: return 'ประเภทที่ขอ';
    }
  }

  get statusOptions(): string[] {
    return this.sourceType === 'training' || this.sourceType === 'exam'
      ? NUMERIC_PAYMENT_STATUSES
      : FORM_PAYMENT_STATUSES;
  }

  get isCancelingPayment(): boolean {
    return this.selectedStatus === 'ยกเลิกการชำระ';
  }

  get willIssueReceipt(): boolean {
    return this.selectedStatus === 'ชำระเงินเสร็จสิ้น' && !!this.record && !this.record.codeReceipt;
  }

  get hasStatusChanged(): boolean {
    return !!this.record && this.selectedStatus !== this.record.paymentStatus;
  }

  get hasReceipt(): boolean {
    return !!this.record?.codeReceipt;
  }

  get slipIsPdf(): boolean {
    return /\.pdf(\?|#|$)/i.test(this.record?.paymentSlip || '');
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'ชำระเงินเสร็จสิ้น': return 'bg-success';
      case 'รอตรวจสอบยอดชำระ': return 'bg-warning text-dark';
      case 'รอชำระเงิน': return 'bg-secondary';
      case 'ไม่ผ่านการตรวจสอบ': return 'bg-danger';
      case 'ยกเลิกการชำระ': return 'bg-dark';
      default: return 'bg-light text-dark';
    }
  }

  getReceiptTypeLabel(receiptType: string | null | undefined): string {
    if (receiptType === 'company') return 'นิติบุคคล';
    if (receiptType === 'individual') return 'บุคคลธรรมดา';
    return '-';
  }

  formatDateTime(value?: string | null): string {
    if (!value) return '-';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '-';
    return `${date.toLocaleDateString('th-TH')} ${date.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })} น.`;
  }

  formatCurrency(amount?: number): string {
    return new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
  }

  /** แอปรันแบบ zoneless จึงต้องสั่ง render ใหม่เองหลังเปลี่ยนค่าใน dropdown */
  onStatusSelectionChanged(): void {
    this.cdr.detectChanges();
  }

  // ── บันทึกสถานะการชำระเงิน ───────────────────────────────────────────────

  saveStatus(): void {
    if (!this.record) return;

    if (!this.hasStatusChanged) {
      this.successMessage = 'สถานะไม่มีการเปลี่ยนแปลง';
      this.cdr.detectChanges();
      return;
    }

    const body: any = { paymentStatus: this.selectedStatus };
    if (this.isCancelingPayment && this.cancelReason.trim()) {
      body.cancelReason = this.cancelReason.trim();
    }

    this.isSaving = true;
    this.error = null;
    this.successMessage = null;
    this.cdr.detectChanges();

    const previousCode = this.record.codeReceipt;
    const apiUrl = `${environment.apiUrl}/payment-verification/${this.sourceType}/${this.recordId}/payment-status`;

    this.http.patch<PaymentVerificationRow>(apiUrl, body).subscribe({
      next: updated => {
        this.ngZone.run(() => {
          this.isSaving = false;
          if (updated) {
            this.record = updated;
            this.selectedStatus = updated.paymentStatus;
          }
          this.successMessage = updated?.codeReceipt && !previousCode
            ? `บันทึกสถานะเรียบร้อย และออกใบเสร็จเลขที่ ${updated.codeReceipt} แล้ว`
            : 'บันทึกสถานะการชำระเงินเรียบร้อยแล้ว';
          this.cdr.detectChanges();
        });
      },
      error: (err: HttpErrorResponse) => {
        this.ngZone.run(() => {
          this.isSaving = false;
          console.error('❌ บันทึกสถานะไม่สำเร็จ:', err);
          this.error = `บันทึกสถานะไม่สำเร็จ: ${err.error?.message || err.message}`;
          this.cdr.detectChanges();
        });
      }
    });
  }

  // ── แก้ไขใบเสร็จ (วันเวลาโอน / ประเภทใบเสร็จ) ────────────────────────────

  openReceiptDialog(): void {
    if (!this.record) return;
    this.receiptTransferDateTime = this.record.transferDateTime || '';
    this.receiptTypeValue = this.record.receiptType === 'company' ? 'company' : 'individual';
    this.isSavingReceipt = false;
    this.modalService.open(this.receiptModal, { size: 'lg', centered: true, backdrop: 'static' });
  }

  saveReceiptInfo(modal: any): void {
    if (!this.record) return;

    this.isSavingReceipt = true;
    this.cdr.detectChanges();

    const apiUrl = `${environment.apiUrl}/payment-verification/${this.sourceType}/${this.recordId}/receipt`;
    this.http.patch<PaymentVerificationRow>(apiUrl, {
      transferDateTime: this.receiptTransferDateTime.trim(),
      receiptType: this.receiptTypeValue
    }).subscribe({
      next: updated => {
        this.ngZone.run(() => {
          this.isSavingReceipt = false;
          if (updated) this.record = updated;
          modal.close();
          this.successMessage = 'บันทึกข้อมูลใบเสร็จเรียบร้อยแล้ว';
          this.cdr.detectChanges();
        });
      },
      error: (err: HttpErrorResponse) => {
        this.ngZone.run(() => {
          this.isSavingReceipt = false;
          console.error('❌ แก้ไขใบเสร็จไม่สำเร็จ:', err);
          this.error = `แก้ไขใบเสร็จไม่สำเร็จ: ${err.error?.message || err.message}`;
          this.cdr.detectChanges();
        });
      }
    });
  }

  // ── ลิงก์ไปหน้าอื่น ──────────────────────────────────────────────────────

  /** ใบคำขอของรายการนี้ — หน้าเดียวกับที่เจ้าหน้าที่ตรวจเอกสารใช้ */
  viewRequestForm(): void {
    const routes: Record<string, string[]> = {
      'member-card': ['/staff/request-regis-new-member-form', this.recordId],
      'renew-member': ['/staff/request-renew-member-form', this.recordId],
      'register-appraiser': ['/staff/request-regis-new-appraiser-form', this.recordId],
      'renew-appraiser': ['/staff/request-renew-appraiser-form', this.recordId],
      training: ['/staff/cpd-hours-management/detail', this.recordId],
      exam: ['/staff/exam-list-management/detail', this.recordId]
    };
    const target = routes[this.sourceType];
    if (target) {
      this.router.navigate(target, { queryParams: { returnUrl: this.router.url } });
    }
  }

  /**
   * ลิงก์กลับไปหน้า worklist พร้อมแท็บ/หน้าเดิม
   *
   * returnUrl ที่หน้า worklist ส่งมามีทั้งแท็บและหน้าของตารางอยู่แล้ว ใช้ค่านี้
   * เป็นปลายทางของปุ่มย้อนกลับบนหน้าใบเสร็จ เพื่อให้พิมพ์ใบเสร็จรายถัดไปต่อได้ทันที
   */
  private get worklistUrl(): string {
    return this.returnUrl || `/staff/payment-verification?tab=${this.sourceType}`;
  }

  /** เปิดหน้าใบเสร็จเพื่อดู/สั่งพิมพ์ (สมัครสอบใช้ id ของ receipt_log) */
  viewReceipt(): void {
    if (!this.record?.codeReceipt) return;

    if (this.sourceType === 'exam') {
      if (!this.record.receiptLogId) {
        this.error = 'ไม่พบข้อมูลใบเสร็จของรายการนี้';
        this.cdr.detectChanges();
        return;
      }
      this.router.navigate(['/staff/renew-slip', this.record.receiptLogId], {
        queryParams: { type: 'exam', returnUrl: this.worklistUrl }
      });
      return;
    }

    this.router.navigate(['/staff/renew-slip', this.recordId], {
      queryParams: {
        type: this.sourceType,
        receiptLogId: this.record.receiptLogId || undefined,
        returnUrl: this.worklistUrl
      }
    });
  }

  goBack(): void {
    this.router.navigateByUrl(this.worklistUrl);
  }

  // ── หน้าต่างดูหลักฐานการชำระเงิน (ย่อ-ขยายได้) ──────────────────────────

  openSlipPreview(): void {
    const url = this.record?.paymentSlip;
    if (!url) return;
    this.slipPreviewUrl = url;
    this.slipPreviewIsPdf = this.slipIsPdf;
    this.resetSlipZoom();
    this.showSlipPreview = true;
    this.cdr.detectChanges();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.showSlipPreview) {
      this.closeSlipPreview();
    }
  }

  closeSlipPreview(): void {
    this.showSlipPreview = false;
    this.slipPreviewUrl = null;
    this.slipDragging = false;
    this.resetSlipZoom();
    this.cdr.detectChanges();
  }

  zoomSlipIn(): void {
    this.setSlipZoom(this.slipZoom + this.SLIP_ZOOM_STEP);
  }

  zoomSlipOut(): void {
    this.setSlipZoom(this.slipZoom - this.SLIP_ZOOM_STEP);
  }

  resetSlipZoom(): void {
    this.slipZoom = 1;
    this.slipOffsetX = 0;
    this.slipOffsetY = 0;
    this.cdr.detectChanges();
  }

  private setSlipZoom(value: number): void {
    const next = Math.min(this.SLIP_ZOOM_MAX, Math.max(this.SLIP_ZOOM_MIN, Math.round(value * 100) / 100));
    if (next === this.slipZoom) return;
    this.slipZoom = next;
    if (next <= 1) {
      this.slipOffsetX = 0;
      this.slipOffsetY = 0;
    }
    this.cdr.detectChanges();
  }

  get slipZoomPercent(): number {
    return Math.round(this.slipZoom * 100);
  }

  onSlipWheel(event: WheelEvent): void {
    event.preventDefault();
    this.setSlipZoom(this.slipZoom + (event.deltaY < 0 ? this.SLIP_ZOOM_STEP : -this.SLIP_ZOOM_STEP));
  }

  onSlipDragStart(event: MouseEvent): void {
    if (this.slipZoom <= 1) return;
    event.preventDefault();
    this.slipDragging = true;
    this.slipDragStartX = event.clientX - this.slipOffsetX;
    this.slipDragStartY = event.clientY - this.slipOffsetY;
  }

  onSlipDragMove(event: MouseEvent): void {
    if (!this.slipDragging) return;
    event.preventDefault();
    this.slipOffsetX = event.clientX - this.slipDragStartX;
    this.slipOffsetY = event.clientY - this.slipDragStartY;
    this.cdr.detectChanges();
  }

  onSlipDragEnd(): void {
    this.slipDragging = false;
  }

  openSlipInNewTab(): void {
    if (this.record?.paymentSlip) {
      window.open(this.record.paymentSlip, '_blank');
    }
  }
}
